/**
 * The single list of UI languages. Adding a language = one entry here + a `<code>.json` file next to every
 * `en.json` (see docs/i18n.md). Components never branch on a specific language.
 */
export interface LocaleDefinition {
  /** BCP 47 code used for i18next, <html lang> and storage. */
  code: string;
  /** Name in its own language, shown in the language menu. */
  name: string;
  /** Short label in its own language, shown on the compact header control. */
  shortName: string;
  dir: 'ltr' | 'rtl';
  /** Locale passed to Intl for dates and relative times. Digits are always Western (latn). */
  intl: string;
  /** Month style in dates: "07 Oct 2026" (short) or "07 أكتوبر 2026" (long). */
  monthStyle: 'short' | 'long';
  /** Between date and time: "07 Oct 2026, 14:10". */
  dateTimeSeparator: string;
  /** Development-only pseudo locales are hidden from production menus. */
  pseudo?: 'long' | 'rtl';
}

export const DEFAULT_LOCALE = 'en';

export const SUPPORTED_LOCALES: readonly LocaleDefinition[] = [
  { code: 'en', name: 'English', shortName: 'EN', dir: 'ltr', intl: 'en-GB', monthStyle: 'short', dateTimeSeparator: ', ' },
  { code: 'ar', name: 'العربية', shortName: 'عربي', dir: 'rtl', intl: 'ar-AE', monthStyle: 'long', dateTimeSeparator: '، ' },
];

/**
 * Development and Storybook only, built from the English strings:
 * en-XA lengthens and brackets every string (overflow, untranslated text); ar-XB is the same, right-to-left.
 */
export const PSEUDO_LOCALES: readonly LocaleDefinition[] = [
  { code: 'en-XA', name: 'Pseudo (long)', shortName: 'XA', dir: 'ltr', intl: 'en-GB', monthStyle: 'short', dateTimeSeparator: ', ', pseudo: 'long' },
  { code: 'ar-XB', name: 'Pseudo (RTL)', shortName: 'XB', dir: 'rtl', intl: 'en-GB', monthStyle: 'short', dateTimeSeparator: ', ', pseudo: 'rtl' },
];

const ALL = [...SUPPORTED_LOCALES, ...PSEUDO_LOCALES];

export function localeDefinition(code: string | undefined): LocaleDefinition {
  return ALL.find((l) => l.code === code) ?? ALL.find((l) => code?.split('-')[0] === l.code) ?? (SUPPORTED_LOCALES[0] as LocaleDefinition);
}

export const isKnownLocale = (code: unknown): code is string => typeof code === 'string' && ALL.some((l) => l.code === code);
export const isRtl = (code: string) => localeDefinition(code).dir === 'rtl';
